import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import OfferAgentBriefing from '@/components/OfferAgentBriefing';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Search, Pencil, Trash2, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', description: '', url: '', commission_type: 'percentage', commission_value: 0, category: '', is_active: true };

const CATEGORIES = ['Pozyczki', 'Zakupy', 'Konkursy', 'Konta bankowe', 'Ubezpieczenia', 'Nieruchomosci', 'Zdrowie', 'Motoryzacja', 'Edukacja', 'Technologia', 'Inwestycje', 'Podroze', 'Finanse', 'Inne'];

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [briefingOffer, setBriefingOffer] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [suggestedSms, setSuggestedSms] = useState('');
  const [suggestedEmail, setSuggestedEmail] = useState(null);
  const [smsVariants, setSmsVariants] = useState([]);
  const [emailVariants, setEmailVariants] = useState([]);

  const fetchProducts = useCallback(async () => {
    try {
      const r = await api.get('/products', { params: { search } });
      setProducts(r.data.items);
    } catch {} finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openCreate = () => { setForm(emptyForm); setEditId(null); setSuggestedSms(''); setSuggestedEmail(null); setDialogOpen(true); };
  const openEdit = (p) => { setForm({ name: p.name, description: p.description, url: p.url, commission_type: p.commission_type, commission_value: p.commission_value, category: p.category || '', is_active: p.is_active }); setEditId(p.id); setDialogOpen(true); };

  const aiAnalyzeUrl = async () => {
    if (!form.url) { toast.error('Wpisz URL afiliacyjny'); return; }
    setAiAnalyzing(true);
    try {
      const r = await api.post('/products/ai-analyze-url', { url: form.url });
      setForm(prev => ({
        ...prev,
        name: r.data.name || prev.name,
        description: r.data.description || prev.description,
        category: r.data.category || prev.category,
        commission_type: r.data.commission_type || prev.commission_type,
        commission_value: r.data.commission_value || prev.commission_value,
        url: r.data.final_url || r.data.url || prev.url,
      }));
      if (r.data.suggested_sms) setSuggestedSms(r.data.suggested_sms);
      if (r.data.suggested_email_subject || r.data.suggested_email_body) {
        setSuggestedEmail({ subject: r.data.suggested_email_subject || '', body: r.data.suggested_email_body || '' });
      }
      setSmsVariants(r.data.suggested_sms_variants || []);
      setEmailVariants(r.data.suggested_email_variants || []);
      toast.success('AI uzupelnil dane produktu!');
      if (r.data.ai_error) toast.warning(`AI fallback: ${r.data.ai_error}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad analizy AI'); }
    finally { setAiAnalyzing(false); }
  };


  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/products/${editId}`, form);
        toast.success('Produkt zaktualizowany');
      } else {
        // If AI generated templates, use new endpoint
        if (suggestedSms || suggestedEmail) {
          const payload = {
            ...form,
            suggested_sms: suggestedSms || '',
            suggested_email_subject: suggestedEmail?.subject || '',
            suggested_email_body: suggestedEmail?.body || '',
          };
          const r = await api.post('/products/with-templates', payload);
          const created = r.data.templates_created || [];
          if (created.length > 0) {
            toast.success(`Produkt dodany + ${created.length} szablon(y) zapisane: ${created.map(t => t.name).join(', ')}`);
          } else {
            toast.success('Produkt dodany');
          }
        } else {
          await api.post('/products', form);
          toast.success('Produkt dodany');
        }
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunac produkt?')) return;
    try { await api.delete(`/products/${id}`); toast.success('Usunieto'); fetchProducts(); }
    catch { toast.error('Blad usuwania'); }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Produkty</h1>
          <p className="text-sm text-muted-foreground mt-1">Zarzadzaj ofertami do promocji</p>
        </div>
        <Button onClick={openCreate} data-testid="products-add-button"><Plus className="h-4 w-4 mr-2" />Dodaj produkt</Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Szukaj produktu..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-card/50" data-testid="products-search-input" />
        </div>
      </div>

      <Card className="border-border/50 bg-card/80">
        <CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div> : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Nazwa</TableHead>
                  <TableHead>Kategoria</TableHead>
                  <TableHead>Prowizja</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Brak produktow. Dodaj pierwszy!</TableCell></TableRow>
                ) : products.map(p => (
                  <TableRow key={p.id} className="hover:bg-muted/30 transition-colors duration-150">
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground">{p.category || '-'}</TableCell>
                    <TableCell className="font-mono text-sm">{p.commission_value}{p.commission_type === 'percentage' ? '%' : ' PLN'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={p.is_active ? 'border-[hsl(var(--success))]/30 text-[hsl(var(--success))]' : 'text-muted-foreground'}>
                        {p.is_active ? 'Aktywny' : 'Nieaktywny'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-purple-400" onClick={() => setBriefingOffer(p)} title="Briefing agenta + ROI" data-testid={`product-briefing-${p.id}`}><Sparkles className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(p.id)} data-testid={`product-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? 'Edytuj produkt' : 'Nowy produkt'}</DialogTitle>
            <DialogDescription>Wklej link afiliacyjny i kliknij AI aby automatycznie uzupelnic dane</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* URL + AI button */}
            <div className="space-y-2">
              <Label>Link afiliacyjny (URL)</Label>
              <div className="flex gap-2">
                <Input value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://link-afiliacyjny.pl/offer/123" className="flex-1" data-testid="product-form-url" />
                <Button variant="outline" onClick={aiAnalyzeUrl} disabled={aiAnalyzing || !form.url} data-testid="product-ai-analyze" className="shrink-0">
                  {aiAnalyzing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                  {aiAnalyzing ? 'Analizuje...' : 'AI Uzupelnij'}
                </Button>
              </div>
              {aiAnalyzing && <p className="text-xs text-muted-foreground">AI odwiedza strone, analizuje tresc i wyciaga dane produktu...</p>}
            </div>
            <div className="space-y-2"><Label>Nazwa produktu</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="product-form-name" placeholder="Wypelni AI lub wpisz recznie" /></div>
            <div className="space-y-2"><Label>Opis</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} data-testid="product-form-description" placeholder="Wypelni AI lub wpisz recznie" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Kategoria</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="product-form-category"><SelectValue placeholder="Wybierz..." /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Typ prowizji</Label>
                <Select value={form.commission_type} onValueChange={v => setForm({...form, commission_type: v})}>
                  <SelectTrigger data-testid="product-form-commission-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Procentowa</SelectItem>
                    <SelectItem value="fixed">Stala kwota</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Wartosc prowizji</Label>
              <Input type="number" value={form.commission_value} onChange={e => setForm({...form, commission_value: parseFloat(e.target.value) || 0})} data-testid="product-form-commission-value" />
            </div>
            {suggestedSms && (
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
                <p className="text-xs text-muted-foreground">Sugerowany SMS (styl osobisty):</p>
                <p className="text-sm font-mono">{suggestedSms}</p>
                {smsVariants.length > 0 && (
                  <div className="space-y-1 pt-1">
                    <p className="text-[10px] text-muted-foreground uppercase">Warianty:</p>
                    {smsVariants.map((v, i) => <p key={i} className="text-xs font-mono text-muted-foreground">{i+1}. {v}</p>)}
                  </div>
                )}
              </div>
            )}
            {suggestedEmail && (
              <div className="p-3 rounded-lg bg-[hsl(var(--info))]/5 border border-[hsl(var(--info))]/20 space-y-2">
                <p className="text-xs text-muted-foreground">Sugerowany email (styl osobisty):</p>
                <p className="text-xs font-medium">Temat: <span className="font-mono">{suggestedEmail.subject}</span></p>
                <div className="text-xs border-t border-border/30 pt-2 mt-1 prose prose-invert prose-xs max-w-none" dangerouslySetInnerHTML={{ __html: suggestedEmail.body }} />
                {emailVariants.length > 0 && (
                  <div className="space-y-1 pt-1">
                    <p className="text-[10px] text-muted-foreground uppercase">Warianty email:</p>
                    {emailVariants.map((v, i) => <p key={i} className="text-xs text-muted-foreground">{i+1}. {v}</p>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleSave} disabled={saving || !form.name} data-testid="product-form-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editId ? 'Zapisz' : 'Dodaj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <OfferAgentBriefing offer={briefingOffer} open={!!briefingOffer} onOpenChange={(o) => !o && setBriefingOffer(null)} />
    </div>
  );
}
